import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Science as DryRunIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';

const fmtNum = (n) => {
  const v = typeof n === 'string' ? Number(n) : n;
  if (v == null || Number.isNaN(v) || v < 0) return '—';
  return v.toLocaleString();
};

/**
 * Cross-cluster tenant data migration recipe. Picks a tenant + its
 * current (source) cluster + a destination cluster, then asks the
 * backend to generate the {@code INSERT INTO ... SELECT FROM
 * remote(...)} SQL sequence. Operator executes manually — same pattern
 * as the schema migration recipe dialog (iteration 16).
 */
const ClickHouseTenantMigrationDialog = ({ open, clusters, assignments, unassignedTenants, onClose }) => {
  const [form, setForm] = useState({ orgId: '', sourceClusterId: '', destClusterId: '' });
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const allOrgs = useMemo(() => {
    const list = [];
    (assignments || []).forEach(a => list.push({
      id: a.organizationId, name: a.organizationName, slug: a.organizationSlug,
      currentClusterId: a.clusterId, currentClusterName: a.clusterName,
    }));
    (unassignedTenants || []).forEach(t => list.push({
      id: t.organizationId, name: t.organizationName, slug: t.organizationSlug,
      currentClusterId: null, currentClusterName: null,
    }));
    return list;
  }, [assignments, unassignedTenants]);

  const selectedOrg = useMemo(
    () => allOrgs.find(o => String(o.id) === String(form.orgId)),
    [allOrgs, form.orgId]);

  // When operator picks an org with an existing assignment, prefill source.
  useEffect(() => {
    if (selectedOrg?.currentClusterId && !form.sourceClusterId) {
      setForm(f => ({ ...f, sourceClusterId: String(selectedOrg.currentClusterId) }));
    }
  }, [selectedOrg, form.sourceClusterId]);

  useEffect(() => {
    if (!open) {
      setForm({ orgId: '', sourceClusterId: '', destClusterId: '' });
      setRecipe(null);
      setError(null);
    }
  }, [open]);

  const generate = useCallback(async () => {
    if (!form.orgId || !form.sourceClusterId || !form.destClusterId) return;
    if (form.sourceClusterId === form.destClusterId) {
      setError('Source and destination clusters must differ.');
      return;
    }
    setLoading(true);
    setError(null);
    setRecipe(null);
    try {
      const res = await billingService.tenantMigrationRecipe(
        Number(form.destClusterId), Number(form.sourceClusterId), Number(form.orgId));
      setRecipe(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Recipe generation failed');
    } finally {
      setLoading(false);
    }
  }, [form]);

  const copy = (sql) => {
    try { navigator.clipboard.writeText(sql); } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Migrate tenant data across clusters
          <HelpIcon docKey="tenant_migration_recipe" />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Select
            fullWidth size="small"
            value={form.orgId}
            onChange={(e) => setForm(f => ({ ...f, orgId: e.target.value, sourceClusterId: '' }))}
            displayEmpty>
            <MenuItem value=""><em>Select tenant…</em></MenuItem>
            {allOrgs.filter(o => o.id !== 0).map(o => (
              <MenuItem key={o.id} value={String(o.id)}>
                {o.name || ('org ' + o.id)}
                {o.slug && <Chip size="small" variant="outlined" label={o.slug} sx={{ ml: 1 }} />}
                {o.currentClusterName && (
                  <Chip size="small" color="primary" variant="outlined"
                        label={`on ${o.currentClusterName}`} sx={{ ml: 1 }} />
                )}
              </MenuItem>
            ))}
          </Select>

          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Source cluster (where historical data lives)</Typography>
              <Select fullWidth size="small"
                      value={form.sourceClusterId}
                      onChange={(e) => setForm(f => ({ ...f, sourceClusterId: e.target.value }))}
                      displayEmpty>
                <MenuItem value=""><em>Select source cluster…</em></MenuItem>
                {(clusters || []).map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name} <Chip size="small" sx={{ ml: 1 }} label={c.status} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Destination cluster</Typography>
              <Select fullWidth size="small"
                      value={form.destClusterId}
                      onChange={(e) => setForm(f => ({ ...f, destClusterId: e.target.value }))}
                      displayEmpty>
                <MenuItem value=""><em>Select destination cluster…</em></MenuItem>
                {(clusters || []).map(c => (
                  <MenuItem key={c.id} value={String(c.id)}
                            disabled={String(c.id) === form.sourceClusterId}>
                    {c.name} <Chip size="small" sx={{ ml: 1 }} label={c.status} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Stack>

          <Button variant="contained" onClick={generate}
                  disabled={loading || !form.orgId || !form.sourceClusterId || !form.destClusterId}
                  startIcon={<DryRunIcon />}>
            {loading ? 'Probing source counts…' : 'Generate recipe'}
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {recipe && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>{recipe.sourceClusterName}</strong> → <strong>{recipe.destClusterName}</strong>:
              move <strong>{fmtNum(recipe.totalRowsToCopy)}</strong> rows of tenant <strong>org {recipe.organizationId}</strong>.
              <Box sx={{ mt: 1 }}>
                {Object.entries(recipe.rowCountsPerTable || {}).map(([t, c]) => (
                  <Chip key={t} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5, fontSize: 11 }}
                        label={`${t}: ${fmtNum(c)}`} />
                ))}
              </Box>
            </Alert>

            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Safety checklist</strong>
              <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                {(recipe.safetyChecklist || []).map((c, i) => (
                  <li key={i}><Typography variant="body2">{c}</Typography></li>
                ))}
              </Box>
            </Alert>

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Run each step manually via clickhouse-client or ad-hoc SQL against the destination cluster\'s leader.
              Replace <code>SOURCE_PASSWORD</code> in every step with the source cluster\'s dbPassword before running.
            </Typography>

            {(recipe.steps || []).map((s, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Chip size="small" label={s.name}
                        color={s.name.includes('cleanup') ? 'warning' : 'primary'} />
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Copy SQL">
                    <button
                      onClick={() => copy(s.sql)}
                      style={{
                        background: 'none', border: '1px solid rgba(127,127,127,0.4)',
                        color: 'inherit', padding: '2px 8px', borderRadius: 4,
                        cursor: 'pointer', fontSize: 11,
                      }}>
                      <CopyIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.25 }} />
                      Copy
                    </button>
                  </Tooltip>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {s.description}
                </Typography>
                <Box component="pre" sx={{
                  m: 0, p: 1.5,
                  background: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                  borderRadius: 1,
                  fontFamily: 'ui-monospace, Menlo, monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: 240,
                  overflow: 'auto',
                }}>
                  {s.sql}
                </Box>
              </Paper>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClickHouseTenantMigrationDialog;
